<div class="selfie-verification-modal">
  <!-- Success Screen for SMS Verification -->
  <div *ngIf="showSuccessScreen" class="success-screen-container">
    <div class="success-content">
      <div class="success-icon-container">
        <div class="success-icon">
          <i class="icon-check success-check"></i>
        </div>
      </div>
      <div class="success-message">
        <p
          [innerHTML]="
            verificationCodeSentSuccessMessage ||
              'Your request for selfie verification code has been submitted for NIDA\'s review, after approval, you will receive a verification code via SMS to resume your application.'
              | translate
          "></p>
      </div>
      <div class="success-action">
        <button
          type="button"
          class="btn btn-outline-secondary btn-block"
          (click)="closeSuccessScreen()">
          {{ 'Close' | translate }}
        </button>
      </div>
    </div>
  </div>

  <!-- ── Main Content ─────────────────────────────────────────────────────── -->
  <ng-container *ngIf="!showSuccessScreen">
    <!-- Camera available view -->
    <div *ngIf="cameraAvailable; else cameraErrorTemplate">
      <div class="modal-header">
        <h4 class="modal-title">{{ 'Face ID Verification' | translate }}</h4>
        <p
          class="modal-subtitle"
          [innerHTML]="
            'Move your face into the frame and follow instructions' | translate
          "></p>
        <button
          type="button"
          class="btn-close"
          [attr.aria-label]="'Close' | translate"
          (click)="cancel()"></button>
      </div>

      <div class="modal-body">
        <!-- Step indicators (hidden during capture/verify/result states) -->
        <div
          class="verification-steps-container"
          *ngIf="
            !isFrozen &&
            !isVerifying &&
            !verificationFailed &&
            !verificationSuccess
          ">
          <div class="verification-steps">
            <!-- Left -->
            <div
              class="step-item"
              [class.active]="currentStep === 'left'"
              [class.completed]="verificationSteps.left.completed">
              <div class="step-icon">
                <i
                  class="fa fa-arrow-left"
                  *ngIf="!verificationSteps.left.completed"></i>
                <i
                  class="fa fa-check"
                  *ngIf="verificationSteps.left.completed"></i>
              </div>
              <div
                class="step-label"
                [innerHTML]="'Turn your face to the left' | translate"></div>
            </div>

            <div
              class="step-connector"
              [class.completed]="verificationSteps.left.completed"></div>

            <!-- Right -->
            <div
              class="step-item"
              [class.active]="currentStep === 'right'"
              [class.completed]="verificationSteps.right.completed">
              <div class="step-icon">
                <i
                  class="fa fa-arrow-right"
                  *ngIf="!verificationSteps.right.completed"></i>
                <i
                  class="fa fa-check"
                  *ngIf="verificationSteps.right.completed"></i>
              </div>
              <div
                class="step-label"
                [innerHTML]="'Turn your face to the right' | translate"></div>
            </div>

            <div
              class="step-connector"
              [class.completed]="verificationSteps.right.completed"></div>

            <!-- Center -->
            <div
              class="step-item"
              [class.active]="currentStep === 'center'"
              [class.completed]="verificationSteps.center.completed">
              <div class="step-icon">
                <i
                  class="fa fa-user"
                  *ngIf="!verificationSteps.center.completed"></i>
                <i
                  class="fa fa-check"
                  *ngIf="verificationSteps.center.completed"></i>
              </div>
              <div
                class="step-label"
                [innerHTML]="'Look straight at the camera' | translate"></div>
            </div>
          </div>
        </div>

        <!-- ── Camera container ─────────────────────────────────────────────── -->
        <div
          class="camera-container"
          [class.minimized]="showPhoneVerification || verificationSuccess">
          <!-- FIX: Camera loading placeholder — shown until the first real video
               frame arrives. Prevents the black "freeze" flash on mobile. -->
          <div *ngIf="isCameraLoading" class="camera-loading">
            <div class="spinner-border text-light" role="status">
              <span class="visually-hidden">{{
                'Starting camera...' | translate
              }}</span>
            </div>
            <p class="camera-loading-text">
              {{ 'Starting camera...' | translate }}
            </p>
          </div>

          <video
            #videoElement
            autoplay
            playsinline
            muted
            class="mirror"
            [style.visibility]="
              isCameraLoading || isFrozen || isVerifying || verificationSuccess
                ? 'hidden'
                : 'visible'
            "
            style="
              width: 100%;
              height: 100%;
              object-fit: cover;
              position: absolute;
              top: 0;
              left: 0;
            "></video>

          <!-- Frozen canvas (after capture, before result) -->
          <canvas
            *ngIf="
              isFrozen &&
              !isVerifying &&
              !verificationFailed &&
              !verificationSuccess
            "
            #canvasElement
            style="width: 100%; height: 100%; object-fit: cover">
          </canvas>

          <!-- Verifying state -->
          <div *ngIf="isVerifying" class="verification-loading">
            <div class="face-outline">
              <img
                *ngIf="capturedImage"
                [src]="capturedImage"
                class="captured-face"
                alt="{{ 'Captured face' | translate }}" />
              <div class="loading-spinner">
                <div class="spinner-border text-primary">
                  <output class="visually-hidden">{{
                    'Loading' | translate
                  }}</output>
                </div>
              </div>
              <div class="loading-text">
                <span [innerHTML]="'Verifying...' | translate"></span>
                <span [innerHTML]="'Please wait' | translate"></span>
              </div>
            </div>
          </div>

          <!-- Face overlay (oval + direction indicator) -->
          <div
            *ngIf="
              !isFrozen &&
              !isVerifying &&
              !verificationFailed &&
              !verificationSuccess &&
              isReady &&
              !isCameraLoading
            "
            class="face-overlay"
            [class.face-detected]="faceDetected"
            [class.step-detecting]="verificationSteps[currentStep].detecting"
            [style.borderColor]="
              verificationSteps[currentStep].detecting
                ? '#28a745'
                : faceDetected
                ? '#FFA500'
                : 'rgba(255,255,255,0.5)'
            ">
            <div
              class="step-direction-indicator"
              [class.detecting]="verificationSteps[currentStep].detecting">
              <i *ngIf="currentStep === 'left'" class="fa fa-arrow-left"></i>
              <i *ngIf="currentStep === 'right'" class="fa fa-arrow-right"></i>
              <i *ngIf="currentStep === 'center'" class="fa fa-user"></i>
            </div>
          </div>

          <!-- Models still loading overlay -->
          <div
            *ngIf="
              !isCameraLoading &&
              !isReady &&
              !isVerifying &&
              !verificationFailed &&
              !verificationSuccess
            "
            class="models-loading-overlay">
            <div class="spinner-border spinner-border-sm text-light me-2"></div>
            <span>{{ 'Loading face detection...' | translate }}</span>
          </div>

          <!-- Verification failed -->
          <div
            *ngIf="verificationFailed && !verificationSuccess"
            class="verification-failed"
            [class.minimized]="showPhoneVerification">
            <div class="face-oval-outline">
              <img
                *ngIf="capturedImage"
                [src]="capturedImage"
                class="captured-face"
                alt="{{ 'Captured face' | translate }}" />
              <div class="error-circle"><i class="fa fa-times"></i></div>
            </div>
          </div>

          <!-- Verification success -->
          <div *ngIf="verificationSuccess" class="verification-success">
            <div class="face-oval-outline success">
              <img
                *ngIf="capturedImage"
                [src]="capturedImage"
                class="captured-face"
                alt="{{ 'Captured face' | translate }}" />
              <div class="success-circle"><i class="fa fa-check"></i></div>
            </div>
          </div>
        </div>
        <!-- /camera-container -->
      </div>
      <!-- /modal-body -->

      <!-- Error banner -->
      <div
        *ngIf="
          verificationFailed && !showPhoneVerification && !verificationSuccess
        "
        class="verification-error-message">
        <i class="fa fa-exclamation-circle error-icon"></i>
        <span [innerHTML]="getDisplayErrorMessage() | translate"></span>
      </div>

      <!-- Phone verification section -->
      <div
        *ngIf="showPhoneVerification && !verificationSuccess"
        class="phone-verification-section">
        <div
          class="phone-verification-message-container d-flex align-items-start gap-2">
          <i class="fa fa-info-circle"></i>
          <span
            [innerHTML]="
              ((citizenPhotoNotAvailableError || phoneNumberRequiredError) &&
              displayedPhoneVerificationMessage
                ? displayedPhoneVerificationMessage
                : phoneNumberSelectionTitle ||
                  'Dear citizen, we couldn\'t verify your identity using Face ID. To continue, select or enter your phone number below'
              ) | translate
            ">
          </span>
        </div>

        <div class="form-group mb-4">
          <label
            class="form-label"
            for="phone-select"
            *ngIf="phoneNumbers.length > 0">
            {{ 'Select Phone Number' | translate }}
          </label>
          <irembogov-select
            id="phone-select"
            *ngIf="phoneNumbers.length > 0"
            [customFormControl]="phoneSelectControl"
            [items]="formattedPhoneNumbers"
            [bindLabel]="'label'"
            [bindValue]="'value'"
            [placeholder]="'Select a phone number' | translate"
            [labelForId]="'phone-select'"
            appendTo=".modal-body"
            (itemSelected)="onPhoneNumberSelected($event)">
          </irembogov-select>

          <div *ngIf="showManualInput || phoneNumbers.length === 0">
            <label class="form-label mt-3" [for]="phoneConfig.id">
              {{ 'Phone Number' | translate }}
            </label>
            <irembogov-phone-number
              [formControl]="phoneFormControl"
              ngDefaultControl
              [id]="phoneConfig.id"
              [countryNoValidation]="phoneConfig.countryNoValidation"
              [preferredCountries]="phoneConfig.preferredCountries"
              [allowDropdown]="true"
              [onlyCountries]="phoneConfig.onlyCountries"
              [initialCountry]="'rw'"
              [placeholder]="phoneConfig.placeholder | translate">
            </irembogov-phone-number>
          </div>
        </div>

        <div class="modal-footer">
          <button
            type="button"
            class="btn btn-outline-dark flex-grow-1"
            (click)="cancel()">
            {{ 'Cancel' | translate }}
          </button>
          <button
            type="button"
            class="btn btn-primary flex-grow-1"
            [disabled]="
              (phoneFormControl.invalid && !phoneFormControl.disabled) ||
              verificationRequestInProgress
            "
            (click)="requestVerificationCode()">
            <span *ngIf="!verificationRequestInProgress">{{
              'Request verification code' | translate
            }}</span>
            <span *ngIf="verificationRequestInProgress">
              <div class="spinner-border spinner-border-sm text-light me-2">
                <output class="visually-hidden">{{
                  'Loading' | translate
                }}</output>
              </div>
              {{ 'Requesting...' | translate }}
            </span>
          </button>
        </div>
      </div>

      <!-- Success footer -->
      <div *ngIf="verificationSuccess" class="modal-footer">
        <button
          type="button"
          class="btn btn-primary w-100"
          (click)="continueWithSuccess()">
          {{ 'Continue' | translate }}
        </button>
      </div>

      <!-- Standard footer -->
      <div
        *ngIf="!showPhoneVerification && !verificationSuccess"
        class="modal-footer">
        <button
          *ngIf="!isFrozen && !isVerifying && !verificationFailed"
          type="button"
          class="btn btn-outline-dark w-100"
          (click)="cancel()">
          {{ 'Cancel' | translate }}
        </button>
        <button
          *ngIf="isVerifying"
          type="button"
          class="btn btn-verifying w-100"
          disabled>
          <div class="spinner-border spinner-border-sm text-light me-2">
            <output class="visually-hidden">{{ 'Loading' | translate }}</output>
          </div>
          {{ 'Verifying ...' | translate }}
        </button>
        <button
          *ngIf="verificationFailed"
          type="button"
          class="btn btn-primary w-100"
          (click)="retryVerification()">
          {{ 'Try Again' | translate }}
        </button>
      </div>
    </div>
    <!-- /cameraAvailable -->

    <!-- Camera error -->
    <ng-template #cameraErrorTemplate>
      <div class="camera-error-content">
        <div class="error-icon-container">
          <div class="error-icon-circle">
            <i class="fa fa-exclamation-triangle error-icon"></i>
          </div>
        </div>
        <h4 class="error-title">{{ 'No camera detected' | translate }}</h4>
        <div class="error-message-container">
          <i class="fa fa-info-circle info-icon"></i>
          <p
            class="error-message"
            [innerHTML]="
              cameraNotAvailableErrorMessage ||
                'It seems your device does not have a camera or it is not detected. Please use a device with a camera or visit your nearest sector office or embassy to proceed with your Civil Registration and Vital Statistics (CRVS) registration.'
                | translate
            "></p>
        </div>
        <div class="error-action">
          <button
            type="button"
            class="btn btn-outline-secondary btn-block"
            (click)="cancel()">
            {{ 'Close' | translate }}
          </button>
        </div>
      </div>
    </ng-template>
  </ng-container>
</div>
